import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { i18next, utils, blocks, view } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import isEqual from 'lodash.isequal';
import getLocation from 'src/js/modules/control-pane/selectors/location';
import highlighter from 'src/js/modules/highlighter';
import segment from 'src/js/modules/analytics/segment';
import mediaAttributes from 'src/js/modules/extensions/common/constants/media-attributes';
import getDefaultTraits from 'src/js/modules/analytics/segment/helpers/get-default-traits';
import useEventListener from 'src/js/modules/utils/custom-hooks/use-event-listener';
import { VARIATION_ATTRIBUTE_NAME } from 'src/js/modules/blocks/constants/common';
import getCaptionForElement from 'src/js/modules/common/helpers/get-caption-for-element';
import isCompositeImage from 'src/js/modules/common/helpers/is-composite-image';
import ResizeHandle from '../../../common/components/resize-handle';
import useHandlePosition from '../../../common/hooks/use-handle-position';
import usePositioningArea from '../../../common/hooks/use-positioning-area';
import useDrag from '../../../common/hooks/use-drag';
import useElementsToStick from '../hooks/use-elements-to-stick';
import resizeHandlePositions from '../../../common/constants/resize-handle-positions';
import collectDOMData from '../../../common/helpers/collect-dom-data';
import getHandleDirectionByPosition from '../../../common/helpers/get-handle-direction-by-position';
import getHandleShapeByPosition from '../../../common/helpers/get-handle-shape-by-position';
import parseWidthToPx from '../../../common/helpers/parse-width-to-px';
import getPositionCustomProperties from '../../../common/helpers/get-position-custom-properties';
import {
  MAX_CONTAINER_HEIGHT,
  MAX_CONTAINER_WIDTH_PERCENTAGE,
  MEDIA_GROUP_ATTRIBUTE,
  MIN_CONTAINER_HEIGHT,
  MIN_CONTAINER_WIDTH,
} from '../constants/media-container';
import restoreScrollPosition from '../helpers/restore-scroll-position';
import getResizeOperations from '../helpers/get-resize-operations';
import getPositionOperations from '../helpers/get-position-operations';
import getElementsWithHeightInRow from '../helpers/get-elements-with-height-in-row';
import findClosestSiblingsByHeight from '../helpers/find-closest-siblings-by-height';
import trackImageResizingCompleted from '../trackers/track-image-resizing-completed';
import getRatio from '../helpers/get-ratio';
import isSingleImageResizingDisabled from '../helpers/is-single-image-resizing-disabled';
import isGallerySliderImage from '../helpers/is-gallery-slider-image';
import handleAspectRatioChanges from '../helpers/handle-aspect-ratio-changes';
import getOverlappedMediaContainerByText from '../helpers/get-overlapped-media-container-by-text';
import getVisibleResizeTriggers from '../helpers/get-visible-resize-triggers';
import getLimitedHeight from '../helpers/get-limited-height';
import isPositioningImage from '../helpers/is-positioning-image';
import getPositioningItems from '../helpers/get-positioning-items';
import {
  freezePosition,
  unfreezePosition,
  fittingInPositioningArea,
} from '../helpers/get-positioning-image-resize-handlers';
import computeResizeCorrection from '../helpers/compute-resize-correction';
import calculateResizedBounds from '../helpers/calculate-resized-bounds';
import getPositioningAreaConstraints from '../helpers/get-positioning-area-constraints';
import ResizeTooltip from './resize-tooltip';
import getPositioningBoundingArea from '../../../common/helpers/get-positioning-bounding-area';
import getClosestPositioningElement from '../helpers/get-closest-positioning-element';
import usePositioningCorrection from '../hooks/use-positioning-correction';
import useCompositeItemContainerRect from '../hooks/use-composite-item-container-rect';
import useWidthCalculator from '../hooks/use-width-calculator';
import useOffsetRatio from '../hooks/use-offset-ratio';
import PositioningAlignment from './positioning-alignment';

const { Highlighter } = designSystem;
const {
  track,
  constants: { events },
} = segment;

const getCaptions = () => ({
  shiftPressed: i18next.t('Release <b>Shift</b> to resize this image only'),
  shiftReleased: i18next.t('Hold <b>Shift</b> to resize all images in a row'),
  default: i18next.t('Drag to resize'),
});

/**
 * This component is memorized to prevent unnecessary re-renders.
 * If you add new props, don't forget to update React.memo
 * comparison function in the end of the file
 * */
const ImageResizeTool = ({
  elementId: liveElementId,
  scrollPosition,
  onActionStart,
  onActionEnd,
  onMouseEnter = Function.prototype,
  onMouseLeave = Function.prototype,
}) => {
  const liveElement = view.accessors.getLiveElement(liveElementId);
  const overlappedMediaContainer = getOverlappedMediaContainerByText(liveElement, true);
  const elementId = overlappedMediaContainer
    ? view.accessors.getLiveElementId(overlappedMediaContainer)
    : liveElementId;

  const { element, blockElement, groupElements, nonResizableRowsCount } = useMemo(
    () => collectDOMData(elementId, MEDIA_GROUP_ATTRIBUTE),
    [elementId]
  );
  const elementsToStick = useElementsToStick({ element, scrollPosition });

  const overlayingItems = getPositioningItems(blockElement);
  const captions = useMemo(getCaptions, []);
  const { calculateWidth } = useWidthCalculator({ element });

  const [shiftPressed, setShiftStatus] = useState(false);
  const [isResizing, setResizingStatus] = useState(false);
  const [tooltipPosition, showTooltip] = useState(null);
  const activeDragHandlerPos = useRef(null);
  const currentAspectRatio = useRef(null);
  const cachedRect = useRef(null);
  const [offsetRatio, setOffsetRatio] = useOffsetRatio(1);
  const constraints = useRef(null);
  const cachedSiblings = useRef([]);
  const cachedRowElements = useRef([]);
  const cachedResizableElements = useRef(new Map());
  const cachedHighlightedElements = useRef([]);
  const lastPositionRef = useRef(null);

  const supportGroupResize = groupElements.length > 1;
  const singleImageResizingDisabled = isSingleImageResizingDisabled(element);
  const isSliderImage = isGallerySliderImage(element);
  const isCompositeItem = isCompositeImage(element);

  const isPositionedItem = isPositioningImage(element);
  const positioningElement = getClosestPositioningElement(element) || element;

  const isBaseImageForPositioning = !isPositionedItem && overlayingItems.length;
  const shouldHandlePositioningImages = isPositionedItem || isBaseImageForPositioning;
  const isMultipleOrGalleryResize =
    (supportGroupResize && shiftPressed) || singleImageResizingDisabled || isSliderImage;

  const indent = highlighter.helpers.highlighterOffset.get();
  const controlPaneLocation = useSelector(getLocation);
  const { axisXAlignment, axisYAlignment, handleCorrectionUpdate } = usePositioningCorrection();
  const handlersConfig = useMemo(
    () => ({
      [resizeHandlePositions.TOP_LEFT]: {
        correctionX: -indent,
        correctionY: -indent,
      },
      [resizeHandlePositions.TOP_CENTER]: {
        correctionY: -indent,
      },
      [resizeHandlePositions.TOP_RIGHT]: {
        correctionX: indent,
        correctionY: -indent,
      },
      [resizeHandlePositions.CENTER_LEFT]: {
        correctionX: -indent,
      },
      [resizeHandlePositions.CENTER_RIGHT]: {
        correctionX: indent,
      },
      [resizeHandlePositions.BOTTOM_LEFT]: {
        correctionX: -indent,
        correctionY: indent,
      },
      [resizeHandlePositions.BOTTOM_CENTER]: {
        correctionY: indent,
      },
      [resizeHandlePositions.BOTTOM_RIGHT]: {
        correctionX: indent,
        correctionY: indent,
      },
    }),
    [indent]
  );

  const [visibleResizeTriggers, setVisibleResizeTriggers] = useState(() =>
    getVisibleResizeTriggers(element, handlersConfig)
  );
  const updateVisibleResizeTriggers = useCallback(() => {
    const updatedVisibleResizeTriggers = getVisibleResizeTriggers(element, handlersConfig);

    setVisibleResizeTriggers((currentVisibleTrigger) => {
      if (isEqual(currentVisibleTrigger, updatedVisibleResizeTriggers)) {
        return currentVisibleTrigger;
      }

      return updatedVisibleResizeTriggers;
    });
  }, [element, handlersConfig]);

  const refsByPos = useMemo(() => {
    const refs = {};
    Object.keys(visibleResizeTriggers).forEach((item) => {
      refs[item] = React.createRef();
    });
    return refs;
  }, [visibleResizeTriggers]);

  const [handlePosition, updateHandlePosition] = useHandlePosition({
    element: positioningElement,
    positions: visibleResizeTriggers,
    controlPaneLocation,
  });

  const { positioningAreaBounds, positioningAreaClipPath, updateClipPath } = usePositioningArea(
    positioningElement,
    scrollPosition
  );

  // If the element is not a composite item, we simply return a rect object with all values
  // set to 0, without performing any calculations.
  const compositeGroupRect = useCompositeItemContainerRect({ element, scrollPosition });

  const cacheSiblingsWithSameHeight = (elements) => {
    cachedSiblings.current.push(...elements);
  };

  const cacheRowElements = () => {
    cachedRowElements.current.push(...getElementsWithHeightInRow(element, groupElements, false));
  };

  const clearCacheRowElements = () => {
    cachedRowElements.current.splice(0, cachedRowElements.current.length);
  };

  const cacheResizableElements = () => {
    groupElements.forEach((resizableElement) => {
      // getContainerBounds element isn't available in case when
      // resizable element is ws-composite-group
      const { width, height } = resizableElement.getContainerBounds
        ? resizableElement.getContainerBounds()
        : resizableElement.getBoundingClientRect();

      cachedResizableElements.current.set(resizableElement, {
        width,
        height,
      });
    });
  };

  const clearCacheResizableElements = () => {
    cachedResizableElements.current = new Map();
  };

  const hideAnchors = () => {
    highlighter.operations.hideAnchors();
    cachedSiblings.current.splice(0, cachedSiblings.current.length);
  };

  const showAnchors = () => {
    const elementsToShowAnchors = [element, ...cachedSiblings.current];
    highlighter.operations.showAnchors(elementsToShowAnchors);
  };

  const hideHighlighter = () => {
    highlighter.operations.hide(cachedHighlightedElements.current);
    cachedHighlightedElements.current = [];
  };

  const hideGroupHighlighter = () => {
    if (!isMultipleOrGalleryResize) return;

    const highlightedElements = cachedHighlightedElements.current.filter((el) => el !== element);
    highlighter.operations.hide(highlightedElements);
    cachedHighlightedElements.current = [element];
  };

  const showHighlighter = (options = {}) => {
    const { isGroup = isMultipleOrGalleryResize, withElementSize = false } = options;
    let highlightedGroupElements = [];

    highlighter.operations.show([element], {
      withElementSize,
      forceUpdate: true,
      labelGap: 'medium',
    });

    if (isGroup && !isSliderImage) {
      highlightedGroupElements = groupElements.filter((groupElement) => groupElement !== element);
      highlighter.operations.show(highlightedGroupElements, { forceUpdate: true });
    }

    cachedHighlightedElements.current = [element, ...highlightedGroupElements];
  };

  const setElementsHeightByCurrent = () => {
    const { height, width } = element.getBoundingClientRect();
    const { maxAspectRatio } = constraints.current;

    cachedResizableElements.current.forEach((bounds, resizableElement) => {
      const limitedHeight = getLimitedHeight({
        width,
        height,
        maxAspectRatio,
      });
      const ratio = getRatio({
        width,
        height: limitedHeight,
      });

      handleAspectRatioChanges({
        element: resizableElement,
        newAspectRatio: ratio,
        bounds,
      });
    });
  };

  const resetElementsHeight = () => {
    const { width } = cachedRect.current;
    const { maxAspectRatio } = constraints.current;

    cachedResizableElements.current.forEach((bounds, resizableElement) => {
      if (resizableElement === element) return;
      const limitedHeight = getLimitedHeight({
        width,
        height: bounds.height,
        maxAspectRatio,
      });

      const ratio = getRatio({
        width,
        height: limitedHeight,
      });

      handleAspectRatioChanges({
        element: resizableElement,
        newAspectRatio: ratio,
        bounds,
      });
    });
  };

  const getConstraints = (resizeDirection) => {
    const { element: parent } = getPositioningBoundingArea(element);
    const parentStyles = window.getComputedStyle(parent, null);
    const styles = window.getComputedStyle(element, null);

    const hasElementNegativeMargins = [
      parseInt(styles.marginRight, 10),
      parseInt(styles.marginLeft, 10),
    ].some((margin) => margin < 0);

    const parentWidth = hasElementNegativeMargins
      ? parent.clientWidth
      : parent.clientWidth -
        parseInt(parentStyles.paddingLeft, 10) -
        parseInt(parentStyles.paddingRight, 10);

    const maxWidth =
      parseWidthToPx(styles.maxWidth, parentWidth) ||
      (parentWidth * MAX_CONTAINER_WIDTH_PERCENTAGE) / 100;
    const initialWidth = element.initialWidthStyleProperty || 'auto';
    const defaultConstraints = {
      maxAspectRatio: element.maxAspectRatio,
      minHeight: MIN_CONTAINER_HEIGHT,
      maxHeight: element.maxAspectRatio
        ? parentWidth / element.maxAspectRatio
        : MAX_CONTAINER_HEIGHT,
      minWidth: parseWidthToPx(styles.minWidth, parentWidth) || MIN_CONTAINER_WIDTH,
      initialWidth: parseWidthToPx(initialWidth, parentWidth) || maxWidth,
      maxWidth,
      parentWidth,
    };

    if (isPositionedItem) {
      return getPositioningAreaConstraints({
        elementBounds: positioningElement.getBoundingClientRect(),
        positioningAreaBounds,
        defaultConstraints,
        resizeDirection,
      });
    }

    return defaultConstraints;
  };

  const handleResize = (currentWidth, currentHeight, aspectRatio, applyLiveChanges) => {
    const { width, height, bottom } = cachedRect.current;
    const { maxAspectRatio, minHeight, minWidth, maxWidth, maxHeight, parentWidth, initialWidth } =
      constraints.current;
    let newWidth = currentWidth !== null ? currentWidth : width;
    let newHeight = Math.round(
      getLimitedHeight({
        width: parentWidth,
        height: currentHeight,
        maxAspectRatio,
        minHeight,
        maxHeight,
      })
    );

    if (currentWidth) {
      newWidth = Math.max(Math.min(Math.round(currentWidth), maxWidth), minWidth);

      if (isPositionedItem) {
        element.width = calculateWidth({ width: newWidth, parentWidth });
      } else {
        element.width =
          newWidth !== initialWidth ? calculateWidth({ width: newWidth, parentWidth }) : null;
      }
    }

    let ratio = getRatio({ width: newWidth, height: newHeight });
    if (aspectRatio) {
      const heightWithKeptRatio = newWidth / aspectRatio;
      if (heightWithKeptRatio < minHeight) {
        ratio = getRatio({ width: newWidth, height: minHeight });
      } else {
        ratio = aspectRatio;
      }
    }

    if (!supportGroupResize) {
      hideAnchors();
      handleAspectRatioChanges({
        element,
        newAspectRatio: ratio,
        bounds: cachedResizableElements.current.get(element),
      });
    }

    if (supportGroupResize) {
      if (isMultipleOrGalleryResize) {
        hideAnchors();
        groupElements.forEach((resizableElement) => {
          const el = resizableElement;

          if (currentWidth || currentHeight) {
            el.width = newWidth !== initialWidth ? `${newWidth}px` : null;
          }

          handleAspectRatioChanges({
            element: el,
            newAspectRatio: ratio,
            bounds: cachedResizableElements.current.get(element),
          });
        });
      } else {
        const matchedElements = findClosestSiblingsByHeight(
          getElementsWithHeightInRow(element, groupElements),
          newHeight
        );

        const [firstMatchedElement] = matchedElements;

        if (firstMatchedElement) {
          newHeight = firstMatchedElement.height;
          if (!aspectRatio || newHeight === minHeight) {
            newHeight = getLimitedHeight({
              width: newWidth,
              height: newHeight,
              maxAspectRatio,
            });
            ratio = getRatio({ width: newWidth, height: newHeight });
          }

          hideAnchors();
          cacheSiblingsWithSameHeight(matchedElements.map(({ element: sibling }) => sibling));
        } else {
          hideAnchors();
        }
        handleAspectRatioChanges({
          element,
          newAspectRatio: ratio,
          bounds: cachedResizableElements.current.get(element),
        });
      }
    }

    // positioned images have absolute positioning, so we don't need to calculate offset ratio
    if (isPositionedItem) {
      setOffsetRatio(1);
    } else {
      const elementRect = element.getBoundingClientRect();
      const heightDelta = Math.abs(Math.floor(elementRect.height) - Math.floor(height));
      const bottomDelta = Math.abs(Math.floor(elementRect.bottom) - Math.floor(bottom));

      setOffsetRatio(bottomDelta === 0 ? 1 : bottomDelta / heightDelta);
    }

    updateHandlePosition(visibleResizeTriggers);

    if (
      cachedSiblings.current.length &&
      activeDragHandlerPos.current === resizeHandlePositions.BOTTOM_CENTER
    ) {
      showAnchors();
    }

    if (!applyLiveChanges) return;

    let resizeOperations = [];
    let positionOperations = [];

    if (isMultipleOrGalleryResize) {
      const biggestHeightInRow = cachedRowElements.current.reduce(
        (acc, itm) => (itm.height > acc ? itm.height : acc),
        0
      );
      const scrollDistance = (newHeight - biggestHeightInRow) * nonResizableRowsCount;

      resizeOperations = getResizeOperations(groupElements);
      restoreScrollPosition(scrollPosition + scrollDistance);
    } else {
      resizeOperations = getResizeOperations([element]);
    }

    if (isPositionedItem) {
      positionOperations = getPositionOperations([positioningElement]);
    }

    if (isBaseImageForPositioning) {
      positionOperations = getPositionOperations(overlayingItems);
    }

    view.operations.bulkViewOperations([...resizeOperations, ...positionOperations]);

    const blockId = blocks.accessors.getBlockIdByElement(blockElement);
    const blockVariationId = blockElement.getAttribute(VARIATION_ATTRIBUTE_NAME);

    trackImageResizingCompleted({
      blockId,
      blockVariationId,
      oldHeight: height,
      newHeight,
      affectedImages: isMultipleOrGalleryResize ? groupElements.length : 1,
      shiftKeyPressed: shiftPressed,
      elementId: getCaptionForElement(element).id,
    });
  };

  const handleDrag = (distance, applyLiveChanges = false) => {
    const { height } = cachedRect.current;
    let newWidth = null;
    let newHeight = height;
    let aspectRatio = null;

    const elementBoundsAfterResize = calculateResizedBounds({
      initialBounds: cachedRect.current,
      resizeDirection: activeDragHandlerPos.current,
      elementOffsetRatio: offsetRatio.current,
      distance,
      initialAspectRatio: currentAspectRatio.current,
    });

    const correction = computeResizeCorrection({
      initialElementBounds: cachedRect.current,
      positioningAreaBounds,
      initialAspectRatio: currentAspectRatio.current,
      distance,
      elementOffsetRatio: offsetRatio.current,
      elements: elementsToStick,
      correctionDistance: 5,
      resizeDirection: activeDragHandlerPos.current,
    });

    switch (activeDragHandlerPos.current) {
      case resizeHandlePositions.TOP_RIGHT:
      case resizeHandlePositions.TOP_LEFT: {
        if (!correction.y) {
          newWidth = elementBoundsAfterResize.width + correction.x;
          newHeight = newWidth / currentAspectRatio.current;
        } else {
          newHeight = elementBoundsAfterResize.height + correction.y;
          newWidth = newHeight * currentAspectRatio.current;
        }

        break;
      }

      case resizeHandlePositions.CENTER_LEFT:
      case resizeHandlePositions.CENTER_RIGHT: {
        newWidth = elementBoundsAfterResize.width + correction.x;
        break;
      }

      case resizeHandlePositions.BOTTOM_RIGHT:
      case resizeHandlePositions.BOTTOM_LEFT: {
        if (!correction.y) {
          newWidth = elementBoundsAfterResize.width + correction.x;
          if (newWidth > MIN_CONTAINER_WIDTH) {
            aspectRatio = currentAspectRatio.current;
          } else if (height > MIN_CONTAINER_HEIGHT) {
            const currentHeight = newWidth / currentAspectRatio.current;
            aspectRatio = getRatio({ width: MIN_CONTAINER_WIDTH, height: currentHeight });
          }
        } else {
          newHeight = elementBoundsAfterResize.height + correction.y;
          newWidth = newHeight * currentAspectRatio.current;

          if (newWidth > MIN_CONTAINER_WIDTH) {
            aspectRatio = currentAspectRatio.current;
          } else if (height > MIN_CONTAINER_HEIGHT) {
            aspectRatio = getRatio({ width: MIN_CONTAINER_WIDTH, height: newHeight });
          }
        }
        break;
      }

      case resizeHandlePositions.TOP_CENTER:
      case resizeHandlePositions.BOTTOM_CENTER:
      default: {
        newHeight = elementBoundsAfterResize.height + correction.y;
        break;
      }
    }

    handleResize(newWidth, newHeight, aspectRatio, applyLiveChanges);
    handleCorrectionUpdate(correction);
  };

  const handleMouseEnter = (e) => {
    const { target } = e;
    onMouseEnter();
    showHighlighter();

    const position = Object.keys(refsByPos).find((pos) => refsByPos[pos]?.current === target);

    if (!isResizing) {
      showTooltip(position);
    }
  };

  const handleMouseLeave = () => {
    onMouseLeave();
    showTooltip(null);
    hideGroupHighlighter();
  };

  const handleStartResizingOfPositioningImages = (direction) => {
    if (isPositionedItem) {
      updateClipPath();
      freezePosition({
        element: positioningElement,
        positioningAreaBounds,
        direction,
      });
    }

    if (isBaseImageForPositioning) {
      lastPositionRef.current = overlayingItems.map((el) => getPositionCustomProperties(el).y);
    }
  };

  const handleResizingOfPositioningImages = () => {
    if (isPositionedItem) {
      updateVisibleResizeTriggers();
      updateClipPath();
    }

    if (isBaseImageForPositioning) {
      overlayingItems.forEach((el, index) =>
        fittingInPositioningArea({
          element: el,
          positioningAreaBounds: getPositioningBoundingArea(el).boundingRect,
          currentY: lastPositionRef.current[index],
        })
      );
    }
  };

  const handleStopResizingOfPositioningImages = () => {
    if (isPositionedItem) {
      unfreezePosition({
        element: positioningElement,
        positioningAreaBounds,
      });
    }

    if (isBaseImageForPositioning) {
      lastPositionRef.current = null;
    }
  };

  useDrag(
    Object.values(refsByPos),
    {
      onStart(event) {
        activeDragHandlerPos.current = Object.keys(refsByPos).find(
          (pos) => refsByPos[pos]?.current === event.target
        );
        currentAspectRatio.current = element.aspectRatio;
        cachedRect.current = element.getBoundingClientRect();
        constraints.current = getConstraints(activeDragHandlerPos.current);

        if (shouldHandlePositioningImages) {
          handleStartResizingOfPositioningImages(activeDragHandlerPos.current);
        }

        setResizingStatus(true);
        showTooltip(null);
        hideHighlighter();
        showHighlighter({ isGroup: isMultipleOrGalleryResize, withElementSize: true });
        cacheRowElements();
        cacheResizableElements();
        onActionStart();
        track(events.IMAGE_RESIZING_INITIATED, {
          ...getDefaultTraits(elementId),
          elementId: getCaptionForElement(element).id,
        });
      },

      onMove(distance) {
        if (shouldHandlePositioningImages) {
          handleResizingOfPositioningImages();
        }

        handleDrag(distance);
        showHighlighter({ isGroup: isMultipleOrGalleryResize, withElementSize: true });
      },

      onEnd(distance) {
        if (shouldHandlePositioningImages) {
          handleStopResizingOfPositioningImages();
        }

        setResizingStatus(false);
        handleDrag(distance, true);
        showHighlighter({ isGroup: isMultipleOrGalleryResize });
        clearCacheRowElements();
        clearCacheResizableElements();
        hideAnchors();
        activeDragHandlerPos.current = null;
        currentAspectRatio.current = null;
        onActionEnd();
      },
    },
    { throttleMove: true }
  );

  const handleKey = ({ shiftKey }) => {
    setShiftStatus(shiftKey);
    if (isResizing) {
      if (shiftKey) {
        hideAnchors();
        setElementsHeightByCurrent();
      } else {
        resetElementsHeight();
      }
    }
    hideHighlighter();
    showHighlighter({ isGroup: shiftKey, withElementSize: isResizing });
  };

  const keyEventCallback =
    supportGroupResize && !singleImageResizingDisabled && !isSliderImage ? handleKey : utils.noop;

  useEventListener('keydown', keyEventCallback, document);
  useEventListener('keyup', keyEventCallback, document);
  useEventListener('keydown', keyEventCallback, element.ownerDocument);
  useEventListener('keyup', keyEventCallback, element.ownerDocument);

  useEffect(() => {
    showHighlighter();

    return () => {
      hideHighlighter();
    };
    // we need this hook only on mount and unmount
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (overlappedMediaContainer) {
      highlighter.operations.show([element]);
    }
  }, [element, overlappedMediaContainer]);

  const setActiveHandlerPosition = useCallback((pos) => {
    activeDragHandlerPos.current = pos;
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      updateVisibleResizeTriggers();
    });

    observer.observe(element, {
      attributes: true,
      attributeFilter: [mediaAttributes.DATA_ALIGNMENT_ATTRIBUTE],
    });

    return () => {
      observer.disconnect();
    };
  }, [element, updateVisibleResizeTriggers]);

  useEffect(() => {
    updateHandlePosition(visibleResizeTriggers);
  }, [updateHandlePosition, visibleResizeTriggers]);

  return (
    <React.Fragment>
      {isPositionedItem && (
        <Highlighter
          appearance="borderless-gradient"
          elementBounds={{
            width: positioningAreaBounds.width,
            height: positioningAreaBounds.height,
            top: positioningAreaBounds.topWithScrollPosition,
            left: positioningAreaBounds.left,
          }}
          clipPath={positioningAreaClipPath}
          visible={isResizing}
        />
      )}
      {isCompositeItem && isResizing && (
        <Highlighter
          appearance="default"
          color="default"
          elementBounds={{
            width: compositeGroupRect.width,
            height: compositeGroupRect.height,
            top: compositeGroupRect.topWithScrollPosition,
            left: compositeGroupRect.left,
          }}
          visible
        />
      )}
      {isResizing && (
        <PositioningAlignment
          scrollPosition={scrollPosition}
          x={axisXAlignment}
          y={axisYAlignment}
        />
      )}
      {Object.keys(handlePosition).map((pos) => {
        const direction = getHandleDirectionByPosition(pos);
        const shape = getHandleShapeByPosition(pos);

        return (
          <ResizeHandle
            key={pos}
            top={handlePosition[pos][1]}
            left={handlePosition[pos][0]}
            shape={shape}
            direction={direction}
            ref={refsByPos[pos]}
            {...(isCompositeItem && { className: 'ws-resize-handle--in-composite-item' })}
            onMouseDown={() => setActiveHandlerPosition(pos)}
            onTouchStart={() => setActiveHandlerPosition(pos)}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        );
      })}
      {tooltipPosition && (
        <ResizeTooltip
          left={handlePosition[tooltipPosition][0]}
          top={handlePosition[tooltipPosition][1]}
          direction={controlPaneLocation.direction}
        >
          {supportGroupResize && !singleImageResizingDisabled && !isSliderImage && (
            <div
              // eslint-disable-next-line yola/react/no-danger
              dangerouslySetInnerHTML={{
                __html: shiftPressed ? captions.shiftPressed : captions.shiftReleased,
              }}
            />
          )}
          {(!supportGroupResize || singleImageResizingDisabled || isSliderImage) &&
            captions.default}
        </ResizeTooltip>
      )}
    </React.Fragment>
  );
};

ImageResizeTool.propTypes = {
  elementId: PropTypes.string.isRequired,
  scrollPosition: PropTypes.number.isRequired,
  onActionStart: PropTypes.func.isRequired,
  onActionEnd: PropTypes.func.isRequired,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
};

const MemorizedImageResizeTool = React.memo(
  ImageResizeTool,
  (prevProps, nextProps) =>
    prevProps.elementId === nextProps.elementId &&
    prevProps.scrollPosition === nextProps.scrollPosition &&
    prevProps.onActionStart === nextProps.onActionStart &&
    prevProps.onActionEnd === nextProps.onActionEnd &&
    prevProps.onMouseEnter === nextProps.onMouseEnter &&
    prevProps.onMouseLeave === nextProps.onMouseLeave
);

MemorizedImageResizeTool.displayName = 'ImageResizeTool';

export default MemorizedImageResizeTool;
